import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TourTooltip } from './TourTooltip';

interface TourStep {
    target: string;
    content: string;
    placement?: 'top' | 'bottom' | 'left' | 'right' | 'bottom-end' | 'top-end' | 'left-start' | 'right-start';
}

interface TourContextType {
    startTour: () => void;
    stopTour: () => void;
    resetTour: () => void;
    isTourActive: boolean;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const useTour = () => {
    const context = useContext(TourContext);
    if (!context) {
        throw new Error('useTour must be used within TourProvider');
    }
    return context;
};

interface TourProviderProps {
    children: React.ReactNode;
}

// Constants
const ALWAYS_SHOW_TOUR = false;
const MIN_WIDTH_FOR_TOUR = 1263;
const INIT_DELAY = 500;
const OBSERVER_TIMEOUT = 10000;
const TOUR_STATUS_KEY = 'tour_status';

export const TourProvider: React.FC<TourProviderProps> = ({ children }) => {
    const { t } = useTranslation();
    const [run, setRun] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);

    // Refs for cleanup and lifecycle management
    const mountedRef = useRef(true);
    const observerRef = useRef<MutationObserver | null>(null);
    const cleanupTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const initTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const resizeHandlerRef = useRef<(() => void) | null>(null);

    // Define tour steps
    const steps: TourStep[] = useMemo(
        () => [
            {
                target: '.tour-step-0',
                content: t('tourUserDetails'),
                placement: 'bottom-end'
            },
            {
                target: '.tour-step-1',
                content: t('tourToggleSidebar'),
                placement: 'bottom-end'
            },
            {
                target: '.tour-step-2',
                content: t('tourToggleTheme'),
                placement: 'bottom-end'
            },
            {
                target: '.tour-step-3',
                content: t('tourToggleFullscreen'),
                placement: 'bottom-end'
            },
            {
                target: '.tour-step-4',
                content: t('tourChangeLanguage'),
                placement: 'bottom-end'
            }
        ],
        [t]
    );

    // Check if screen size supports tour
    const isScreenSizeValid = useCallback(() => {
        return window.innerWidth > MIN_WIDTH_FOR_TOUR;
    }, []);

    // Check if tour has been seen
    const hasTourBeenSeen = useCallback(() => {
        try {
            return localStorage.getItem(TOUR_STATUS_KEY) === 'true' && !ALWAYS_SHOW_TOUR;
        } catch {
            return false;
        }
    }, []);

    // Mark tour as done
    const markTourCompleted = useCallback(() => {
        try {
            localStorage.setItem(TOUR_STATUS_KEY, 'true');
        } catch (error) {
            console.warn('Failed to save tour status:', error);
        }
    }, []);

    // Cleanup observer and timers
    const cleanupObserver = useCallback(() => {
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }

        if (cleanupTimeoutRef.current) {
            clearTimeout(cleanupTimeoutRef.current);
            cleanupTimeoutRef.current = null;
        }
    }, []);

    // Check if all required elements exist
    const checkAllElementsExist = useCallback((): boolean => {
        return steps.every((step) => document.querySelector(step.target) !== null);
    }, [steps]);

    // Start tour
    const startTour = useCallback(() => {
        if (!mountedRef.current || !isScreenSizeValid()) {
            return;
        }

        // Cleanup observer to prevent race condition
        cleanupObserver();
        setCurrentStep(0);
        setRun(true);
    }, [isScreenSizeValid, cleanupObserver]);

    // Stop tour
    const stopTour = useCallback(() => {
        if (!mountedRef.current) return;
        markTourCompleted();
        setRun(false);
        cleanupObserver();
    }, [markTourCompleted, cleanupObserver]);

    // Reset tour
    const resetTour = useCallback(() => {
        try {
            localStorage.removeItem(TOUR_STATUS_KEY);
        } catch (error) {
            console.warn('Failed to reset tour status:', error);
        }

        if (mountedRef.current && isScreenSizeValid()) {
            cleanupObserver();
            setCurrentStep(0);
            setRun(true);
        }
    }, [isScreenSizeValid, cleanupObserver]);

    // Handle next step
    const handleNext = useCallback(() => {
        if (!mountedRef.current) return;
        if (currentStep < steps.length - 1) {
            setCurrentStep((prev) => prev + 1);
        } else {
            stopTour();
        }
    }, [currentStep, steps.length, stopTour]);

    // Handle previous step
    const handlePrevious = useCallback(() => {
        if (!mountedRef.current) return;
        if (currentStep > 0) {
            setCurrentStep((prev) => prev - 1);
        }
    }, [currentStep]);

    // Handle skip
    const handleSkip = useCallback(() => {
        stopTour();
    }, [stopTour]);

    // Wait for elements to appear in DOM
    const waitForElements = useCallback(() => {
        if (!mountedRef.current || !isScreenSizeValid()) {
            return;
        }

        if (checkAllElementsExist()) {
            startTour();
            return;
        }

        if (observerRef.current) {
            return;
        }

        try {
            observerRef.current = new MutationObserver(() => {
                if (!mountedRef.current || !isScreenSizeValid()) {
                    cleanupObserver();
                    return;
                }

                if (checkAllElementsExist()) {
                    startTour();
                }
            });

            observerRef.current.observe(document.body, {
                childList: true,
                subtree: true,
                attributes: false,
                characterData: false
            });

            // Set a timeout to clean up observer if it doesn't find elements
            cleanupTimeoutRef.current = setTimeout(() => {
                cleanupObserver();
            }, OBSERVER_TIMEOUT);
        } catch (error) {
            console.error('Failed to create MutationObserver:', error);
            cleanupObserver();
        }
    }, [isScreenSizeValid, checkAllElementsExist, startTour, cleanupObserver]);

    // Initialize tour
    useEffect(() => {
        mountedRef.current = true;

        if (!isScreenSizeValid() || hasTourBeenSeen()) {
            return;
        }

        initTimeoutRef.current = setTimeout(() => {
            if (!mountedRef.current || !isScreenSizeValid()) {
                return;
            }

            if (checkAllElementsExist()) {
                startTour();
            } else {
                waitForElements();
            }
        }, INIT_DELAY);

        return () => {
            if (initTimeoutRef.current) {
                clearTimeout(initTimeoutRef.current);
                initTimeoutRef.current = null;
            }
        };
    }, [isScreenSizeValid, hasTourBeenSeen, checkAllElementsExist, startTour, waitForElements]);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            if (!isScreenSizeValid() && run) {
                setRun(false);
                cleanupObserver();
            }
        };

        resizeHandlerRef.current = handleResize;
        window.addEventListener('resize', handleResize);

        return () => {
            if (resizeHandlerRef.current) {
                window.removeEventListener('resize', resizeHandlerRef.current);
                resizeHandlerRef.current = null;
            }
        };
    }, [run, isScreenSizeValid, cleanupObserver]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            mountedRef.current = false;

            if (initTimeoutRef.current) {
                clearTimeout(initTimeoutRef.current);
                initTimeoutRef.current = null;
            }

            cleanupObserver();
        };
    }, [cleanupObserver]);

    // Memoize context value to prevent re-renders
    const contextValue = useMemo<TourContextType>(
        () => ({
            startTour,
            stopTour,
            resetTour,
            isTourActive: run
        }),
        [startTour, stopTour, resetTour, run]
    );

    // Render tour tooltip only when conditions are met
    const shouldShowTooltip = run && currentStep < steps.length && isScreenSizeValid();

    return (
        <TourContext.Provider value={contextValue}>
            {children}
            {shouldShowTooltip && (
                <TourTooltip
                    step={steps[currentStep]}
                    currentStep={currentStep}
                    totalSteps={steps.length}
                    onNext={handleNext}
                    onPrevious={handlePrevious}
                    onSkip={handleSkip}
                    onClose={stopTour}
                />
            )}
        </TourContext.Provider>
    );
};
